import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';
import { handleUnauthorized, waitForLoginCompletion } from './utils';
import { showToast } from '@/components/Toast';
// import { toast } from '@/hooks/ui/use-toast';
/**
 * Axios Instance Configuration
 * 配置好的 axios 实例，包含拦截器处理
 */
// Error notification deduplication
let lastErrorTime = 0;
let lastErrorMessage = '';
const ERROR_THROTTLE_MS = 3000; // 3 seconds throttle for duplicate errors
/**
 * Show error toast with deduplication
 * Prevents showing the same error multiple times within a short time period
 */
function showErrorToast(message: string) {
  const now = Date.now();
  // Skip if same error was shown recently
  if (message === lastErrorMessage && now - lastErrorTime < ERROR_THROTTLE_MS) {
    return;
  }
  // Show toast
  showToast(message, 'error');
  // Update deduplication tracking
  lastErrorTime = now;
  lastErrorMessage = message;
}
// 创建 axios 实例
const axiosInstance = axios.create({
  baseURL: '/',
  timeout: 30000, // 30 秒超时
  headers: {
    'Content-Type': 'application/json',
  },
});
/**
 * 请求拦截器
 * 自动添加 Authorization header
 * 等待登录完成后再发起请求
 */
axiosInstance.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    // 等待登录完成（如果正在登录）
    await waitForLoginCompletion();
    // 从 localStorage 获取 token
    const token = localStorage.getItem('user-token');
    // 如果有 token，添加到请求头
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    // 添加当前时区（IANA 格式，如 "Asia/Shanghai"）
    if (config.headers) {
      config.headers['Timezone'] =
        Intl.DateTimeFormat().resolvedOptions().timeZone;
    }
    return config;
  },
  (error: AxiosError) => {
    return Promise.reject(error);
  }
);
/**
 * 响应拦截器
 * 统一处理错误和 401 未授权
 */
axiosInstance.interceptors.response.use(
  response => {
    // 成功响应直接返回
    return response;
  },
  async (error: AxiosError) => {
    // ONLY handle 401 Unauthorized errors
    // Do NOT handle timeouts (ECONNABORTED), network errors, or other issues
    if (error.response && error.response.status === 401) {
      const resData: any = error.response.data;
      handleUnauthorized(resData?.redirects);
      return Promise.reject(new Error('Unauthorized - redirecting to login'));
    }
    // Handle other HTTP errors with user-friendly messages
    if (error.response) {
      const status = error.response.status;
      const resData: any = error.response.data;
      let errorMessage = '';

      // 优先使用服务器返回的错误消息
      if (resData && (resData.message || resData.detail?.[0]?.msg)) {
        errorMessage = resData.message || resData.detail?.[0]?.msg;
      } else {
        switch (status) {
          case 400:
            errorMessage = 'Bad request. Please check your input.';
            break;
          case 403:
            errorMessage = 'Access forbidden. You do not have permission.';
            break;
          case 404:
            errorMessage = 'Resource not found.';
            break;
          case 500:
            errorMessage = 'Server error. Please try again later.';
            break;
          case 502:
            errorMessage = 'Bad gateway. Please try again later.';
            break;
          case 503:
            errorMessage = 'Service unavailable. Please try again later.';
            break;
          default:
            if (status >= 500) {
              errorMessage = `Server error (${status}). Please try again later.`;
            } else if (status >= 400) {
              errorMessage = `Request error (${status}). Please check your request.`;
            }
        }
      }
      if (errorMessage) {
        showErrorToast(errorMessage);
      }
      console.error(`axios interceptor: Error ${status}`, resData);
    } else if (error.code === 'ECONNABORTED') {
      // Timeout error
      showErrorToast('Request timeout. Please check your connection.');
      console.error('axios interceptor: Request timeout');
    } else if (!error.response) {
      // Network error
      showErrorToast('Network error. Please check your connection.');
      console.error('axios interceptor: Network error or no response');
    }
    // Other errors are directly rejected without logout
    return Promise.reject(error);
  }
);
export default axiosInstance;
